import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Doughnut, Bar, getElementAtEvent } from "react-chartjs-2";
import { Button, DatePicker, Select } from "antd";
import AdminJewelryReportService from "../../../services/admin-jewelryReport-service";
import { bucketTopN } from "../../ChartsGroup/DiamondReportsCharts/_shared/bucketTopN";
import CustomLegend from "../../ChartsGroup/DiamondReportsCharts/_shared/CustomLegend";
import ChartState from "../../ChartsGroup/DiamondReportsCharts/_shared/ChartState";
import { donutOptions, barOptions } from "../../ChartsGroup/DiamondReportsCharts/_shared/chartTheme";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import JewelryDetailSearchTable from "../../reports/jewelry/_shared/JewelryDetailSearchTable";
import { extractDealerIds } from "../../reports/jewelry/_shared/jewelryHelpers";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Admin flagship redesign. This is the page with the documented Vendor/
// Retailer swap bug (Azure DevOps): the pre-redesign JSX rendered the
// "Based on Vendor" panel from topRetailerDealer/topRetailerTotal (fetched
// via GetClicksBasedOnRetailer) and the "Based on Retailer" panel from
// topVendorDealer/topVendorTotal (fetched via GetClicksBasedOnVendor) --
// data was fetched correctly, only the two panels' heading/data pairing was
// swapped. Fixed here simply by wiring each panel to its own matching fetch
// during the rebuild (Category/Price/Vendor/Retailer all render straight
// from their own state, no cross-wiring). Admin always sees all 4 panels
// (no membership-based hide/show, unlike Dealer). reporttype "Global" is
// sent to the Admin_JewelryReportForTableChart family for Admin too --
// that's the existing convention (see AdminJewelryReportTableChart_Global),
// not something introduced here.
const bandStart = (label) => {
  if (!label || label === "Call For Price") return Infinity;
  const match = String(label).replace(/,/g, "").match(/[\d.]+/);
  return match ? Number(match[0]) : Infinity;
};

const parsePriceBand = (label) => {
  if (label === "Call For Price") return { price: "Call For Price", price1: "", price2: "" };
  const [low, high] = label.split("-");
  const lowPrice1 = parseInt(low.replace(/^.*?\$/, "").replace(/,/g, "").trim(), 10);
  const highPrice2 = parseInt((high || "").replace(/[$,]/g, ""), 10);
  return {
    price: "",
    price1: low.includes("Under") ? "0" : lowPrice1,
    price2: low.includes("Under") ? lowPrice1 : highPrice2 || "",
  };
};

const emptyFilter = { category: "", vendor: "", retailer: "", price: "", price1: "", price2: "" };

const JewelryChartReportAdmin = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const forDealer = 3; // Admin always reports as forDealer=3, never changes
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [retailerIds, setRetailerIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);

  const [categoryData, setCategoryData] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [vendorClicksData, setVendorClicksData] = useState([]);
  const [retailerClicksData, setRetailerClicksData] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [totalClicks, setTotalClicks] = useState(0);

  const [filter, setFilter] = useState(emptyFilter);
  const [refetchToken, setRefetchToken] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);

  const categoryChartRef = useRef();
  const priceChartRef = useRef();
  const vendorChartRef = useRef();
  const retailerChartRef = useRef();

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      handleLoadAllCharts(id, 3, dataRangeFrom, dataRangeTo, [], []);
    }
    handleGetVendorDropdownData();
    handleGetRetailerDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetVendorDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    AdminJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "Global" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleGetRetailerDropdownData = () => {
    AdminJewelryReportService.GetRetailerDropdownList({ dealerId: 0, reporttype: "Global" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
    if (values.length === 0) {
      handleGetRetailerDropdownData();
    } else {
      AdminJewelryReportService.ChangeRetailerList({ vendorIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setRetailerList(response.data.responseData);
      });
    }
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
    if (values.length === 0) {
      handleGetVendorDropdownData();
    } else {
      AdminJewelryReportService.ChangeVendorList({ retailerIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setVendorList(response.data.responseData);
      });
    }
  };

  const handleLoadAllCharts = (dealerIdArg, forDealerArg, fromDate, toDate, vIds, rIds) => {
    setChartsLoading(true);
    const base = {
      dealerID: dealerIdArg,
      forDealer: forDealerArg,
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
    };
    Promise.allSettled([
      AdminJewelryReportService.GetClicksBasedOnCategory(base),
      AdminJewelryReportService.GetClicksBasedOnPrice(base),
      AdminJewelryReportService.GetClicksBasedOnVendor({ ...base, reporttype: "Global" }),
      AdminJewelryReportService.GetClicksBasedOnRetailer({ ...base, reporttype: "Global" }),
    ]).then(([categoryRes, priceRes, vendorRes, retailerRes]) => {
      const okData = (settled) => {
        if (settled.status !== "fulfilled") {
          console.error("Admin jewelry chart fetch failed:", settled.reason);
          return [];
        }
        return settled.value.data.message === "Success" ? settled.value.data.responseData || [] : [];
      };
      const category = okData(categoryRes);
      const price = okData(priceRes);
      const vendor = okData(vendorRes);
      const retailer = okData(retailerRes);
      setCategoryData(category);
      setPriceData(price);
      setVendorClicksData(vendor);
      setRetailerClicksData(retailer);
      setTotalClicks(category.reduce((sum, row) => sum + Number(row.count || 0), 0));
      setChartsLoading(false);
    });
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilter(emptyFilter);
    handleLoadAllCharts(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds, retailerIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t("Jewelry Chart Reports"),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [`Vendors: ${vendorNames}`, `Retailers: ${retailerNames}`, `Total Clicks: ${totalClicks}`],
        fileName: "Admin-Jewelry-Chart-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleCategoryClick = (category) => setFilter({ ...emptyFilter, category });
  const handleVendorClick = (vendor) => setFilter({ ...emptyFilter, vendor });
  const handleRetailerClick = (retailer) => setFilter({ ...emptyFilter, retailer });
  const handlePriceClick = (label) => setFilter({ ...emptyFilter, ...parsePriceBand(label) });

  const categoryChart = useMemo(
    () => bucketTopN(categoryData, { labelKey: "category", valueKey: "count", chartLabel: "Category", othersLabel: t("Others") }),
    [categoryData, t]
  );
  const vendorChart = useMemo(
    () => bucketTopN(vendorClicksData, { labelKey: "dealerCompany", valueKey: "total", chartLabel: "Vendor", othersLabel: t("Others") }),
    [vendorClicksData, t]
  );
  const retailerChart = useMemo(
    () => bucketTopN(retailerClicksData, { labelKey: "dealerCompany", valueKey: "total", chartLabel: "Retailer", othersLabel: t("Others") }),
    [retailerClicksData, t]
  );
  const priceRows = useMemo(() => {
    const list = priceData.filter((item) => Number(item.total) > 0);
    const sorted = [...list].sort((a, b) => bandStart(a.range) - bandStart(b.range));
    const total = sorted.reduce((sum, item) => sum + Number(item.total), 0);
    return sorted.map((item) => ({
      label: item.range,
      value: Number(item.total),
      percentage: total > 0 ? ((Number(item.total) / total) * 100).toFixed(1) : 0,
    }));
  }, [priceData]);
  const priceChartData = [["Price Band", "Count"], ...priceRows.map((r) => [`${r.label} (${r.percentage}%)`, r.value])];

  const makeClickHandler = (ref, rows, onPick) => (event) => {
    if (!ref.current) return;
    const [el] = getElementAtEvent(ref.current, event);
    if (!el) return;
    const label = rows[el.index]?.[0]?.replace(/\s*\([^)]*\)\s*$/, "");
    if (label && label !== t("Others")) onPick(label);
  };

  const topCategory = [...categoryData].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const topPriceBand = [...priceRows].sort((a, b) => b.value - a.value)[0];
  const topVendor = [...vendorClicksData].sort((a, b) => Number(b.total || 0) - Number(a.total || 0))[0];
  const topRetailer = [...retailerClicksData].sort((a, b) => Number(b.total || 0) - Number(a.total || 0))[0];

  return (
    <div className="pendjewelreq__section form__fields">
      <div className="row magento__mapping">
        <div className="col-md-12">
          <div className="edr-pagehead-row">
            <div className="edr-pagehead">
              <h1 className="edr-pagehead-title">{t("Jewelry Chart Reports")}</h1>
              <div className="edr-pagehead-sub">
                {moment(dataRangeFrom).format("MMM D, YYYY")} – {moment(dataRangeTo).format("MMM D, YYYY")}
              </div>
            </div>
            <div className="edr-pagehead-actions">
              <Button className="ghost-btn" onClick={handleDownloadChart} loading={pdfLoading}>
                <i className="fa fa-download"></i> {t("Download Chart")}
              </Button>
              <button className="primary-btn" onClick={handleApplyFilter}>{t("Apply Filter")}</button>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="row edr-filters">
            <div className="col-md-3">
              <div className="input__block dataRange-input-block datepicker__block">
                <label>{t("Date Range")}</label>
                <RangePicker
                  className="date-range-input"
                  format={"MM-DD-YYYY"}
                  onChange={onDateChange}
                  defaultValue={[moment().subtract(30, "days"), moment()]}
                  defaultPickerValue={[moment().subtract(30, "days"), moment()]}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="input__block">
                <label>{t("Vendor(s)")}</label>
                <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                  {vendorList.map((item) => (
                    <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="input__block">
                <label>{t("Retailer(s)")}</label>
                <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Retailers")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                  {retailerList.map((item) => (
                    <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="edr-kpis">
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Total Clicks")}</div>
              <div className="edr-kpi-val">{chartsLoading ? "—" : totalClicks.toLocaleString()}</div>
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Category")}</div>
              <div className="edr-kpi-val">{topCategory?.category ?? "—"}</div>
              {topCategory && <div className="edr-kpi-sub">{Number(topCategory.count).toLocaleString()} {t("clicks")}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Price Sweet Spot")}</div>
              <div className="edr-kpi-val">{topPriceBand?.label ?? "—"}</div>
              {topPriceBand && <div className="edr-kpi-sub">{topPriceBand.value.toLocaleString()} {t("clicks")}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Vendor")}</div>
              <div className="edr-kpi-val">{topVendor?.dealerCompany ?? "—"}</div>
              {topVendor && <div className="edr-kpi-sub">{Number(topVendor.total).toLocaleString()} {t("clicks")}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Retailer")}</div>
              <div className="edr-kpi-val">{topRetailer?.dealerCompany ?? "—"}</div>
              {topRetailer && <div className="edr-kpi-sub">{Number(topRetailer.total).toLocaleString()} {t("clicks")}</div>}
            </div>
          </div>
        </div>

        {topCategory && (
          <div className="col-md-12">
            <div className="edr-insight">
              💡 {t("Demand profile this period")}: {t("shoppers gravitate to")} <b>{topCategory.category}</b>
              {topPriceBand && <> {t("in the")} <b>{topPriceBand.label}</b> {t("band")}</>}
              {topVendor && <>, {t("with")} <b>{topVendor.dealerCompany}</b> {t("the standout vendor")}</>}
              {topRetailer && <> {t("and")} <b>{topRetailer.dealerCompany}</b> {t("the standout retailer")}</>}.
            </div>
          </div>
        )}

        <div className="col-md-12" id="pdf-content">
          <div className="row">
            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Category")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && categoryChart.chartData.length > 1 ? (
                    <>
                      <CustomLegend chartData={categoryChart.chartData} colorData={categoryChart.colors} />
                      <div className="edr-chartwrap edr-tall">
                        <Doughnut
                          ref={categoryChartRef}
                          data={{
                            labels: categoryChart.chartData.slice(1).map((r) => r[0]),
                            datasets: [{ data: categoryChart.chartData.slice(1).map((r) => r[1]), backgroundColor: categoryChart.colors, borderColor: "#fff", borderWidth: 2 }],
                          }}
                          options={donutOptions}
                          onClick={makeClickHandler(categoryChartRef, categoryChart.chartData.slice(1), handleCategoryClick)}
                        />
                      </div>
                    </>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Price")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && priceRows.length > 0 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={priceChartRef}
                        data={{
                          labels: priceChartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: priceChartData.slice(1).map((r) => r[1]), backgroundColor: "#3b5bdb", borderRadius: 4 }],
                        }}
                        options={barOptions(false)}
                        onClick={makeClickHandler(priceChartRef, priceChartData.slice(1), handlePriceClick)}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Vendor")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && vendorChart.chartData.length > 1 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={vendorChartRef}
                        data={{
                          labels: vendorChart.chartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: vendorChart.chartData.slice(1).map((r) => r[1]), backgroundColor: vendorChart.colors, borderRadius: 4 }],
                        }}
                        options={barOptions(true)}
                        onClick={makeClickHandler(vendorChartRef, vendorChart.chartData.slice(1), handleVendorClick)}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Retailer")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && retailerChart.chartData.length > 1 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={retailerChartRef}
                        data={{
                          labels: retailerChart.chartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: retailerChart.chartData.slice(1).map((r) => r[1]), backgroundColor: retailerChart.colors, borderRadius: 4 }],
                        }}
                        options={barOptions(true)}
                        onClick={makeClickHandler(retailerChartRef, retailerChart.chartData.slice(1), handleRetailerClick)}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <JewelryDetailSearchTable
            dealerId={dealerId}
            forDealer={forDealer}
            dataRangeFrom={dataRangeFrom}
            dataRangeTo={dataRangeTo}
            vendorIds={vendorIds}
            retailerIds={retailerIds}
            dealerMembershipType="Admin"
            filterParamName={filter.category ? "category" : filter.vendor ? "vendor" : filter.retailer ? "retailer" : "price"}
            filterValue={filter.category || filter.vendor || filter.retailer || filter.price}
            price1={filter.price1}
            price2={filter.price2}
            fileNamePart="AdminJewelryChartReports"
            dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
            refetchToken={refetchToken}
            reportType="Global"
            title={t("Product Click Log")}
          />
        </div>
      </div>
    </div>
  );
};

export default JewelryChartReportAdmin;
